import { EMPTY_STRING, Nullable } from '@rotomeca/utils';
import BnumElement from '../../bnum-element';
import { HTMLBnumButton } from '../button/bnum-button';
import { HTMLBnumIcon } from '../icon/bnum-icon';
import { TAG_ICON_BUTTON } from '../../../core/utils/constants/tags';
import JsEvent from '@rotomeca/event';
import { REG_XSS_SAFE } from '../../../core/utils/constants/regexp';
import { Define, Self } from '../../../core/decorators';
import Log from '../../../core/utils/Log';
import { Observe } from 'core/decorators/observe';
import { Listener } from 'core/decorators/listener';
import { OnLinkedClickEventInitializer } from './bnum-icon-button.internal/listeners';
import style from './bnum-icon-button.less';

//#region Global Constants
const ID_ICON = 'icon';
//#endregion Global Constants

const TEMPLATE = (
	<HTMLBnumIcon id={ID_ICON}>
		<slot></slot>
	</HTMLBnumIcon>
);

/**
 * Button contenant une icône.
 *
 * @category Buttons
 *
 * @structure Button Icon
 * <bnum-icon-button>home</bnum-icon-button>
 *
 * @structure Button Disabled
 * <bnum-icon-button disabled>home</bnum-icon-button>
 *
 * @cssvar {0.6} --bnum-button-disabled-opacity - Opacité du bouton désactivé
 * @cssvar {none} --bnum-button-disabled-pointer-events - Gestion des événements souris pour le bouton désactivé
 *
 * @slot (default) - Contenu de l'icône (nom de l'icône à afficher)
 */
@Define({ styles: style, tag: TAG_ICON_BUTTON, template: TEMPLATE })
@Observe('click')
export class HTMLBnumButtonIcon extends BnumElement {
	//#region Private fields
	/**
	 * Référence vers l'élément icône à l'intérieur du bouton
	 */
	#_icon: HTMLBnumIcon | null = null;
	#_lastClick: Nullable<string> = null;
	//#endregion Private fields

	//#region Getters/Setters
	/** Référence à la classe HTMLBnumButtonIcon */
	@Self _!: typeof HTMLBnumButtonIcon;

	@Listener(OnLinkedClickEventInitializer, { lazy: false })
	accessor #_linkedClickEvent!: JsEvent<(click: string) => void>;

	/**
	 * Référence vers l'élément icône à l'intérieur du bouton.
	 *
	 * Si l'icône n'a pas été mise en mémoire, elle sera cherché puis mise en mémoire.
	 */
	get #_iconElement(): HTMLBnumIcon {
		if (!this.#_icon) {
			const icon =
				this.querySelector(HTMLBnumIcon.TAG) ??
				this.shadowRoot?.getElementById(ID_ICON);

			if (!icon) this.#_throw('Icon element not found inside icon button');

			this.#_icon = icon as HTMLBnumIcon;
		}

		return this.#_icon;
	}

	/**
	 * Icône affichée dans le bouton
	 */
	get icon() {
		return (
			(this.#_iconElement.icon || this.#_throw('Icon is not defined')) ??
			EMPTY_STRING
		);
	}

	set icon(value: string) {
		this.#_iconElement.icon = value;
	}
	//#endregion Getters/Setters

	//#region Lifecycle
	constructor() {
		super();
	}

	/**
	 * @inheritdoc
	 */
	protected _p_buildDOM(): void {
		HTMLBnumButton.ToButton(this);

		if (this.title === EMPTY_STRING)
			Log.warn(
				this._.TAG,
				'Icon button should have a title for accessibility purposes',
			);

		if (this.hasAttribute('click')) {
			const click = this.getAttribute('click');
			this.#_updateAttributeClick(click ?? EMPTY_STRING);
		}
	}

	protected _p_update(
		name: string,
		oldVal: string | null,
		newVal: string | null,
	): void | Nullable<'break'> {
		if (oldVal === newVal) return;

		if (name === 'click') {
			this.#_updateAttributeClick(newVal ?? EMPTY_STRING);
		}
	}
	//#endregion Lifecycle

	//#region Private methods
	#_updateAttributeClick(val: string) {
		if (val !== this.#_lastClick) {
			this.#_lastClick = val;

			if (this.#_linkedClickEvent.has('click'))
				this.#_linkedClickEvent.remove('click');

			if (val && REG_XSS_SAFE.test(val)) {
				this.#_linkedClickEvent.add(
					'click',
					(click: string) => {
						// Si c'est un id unique
						const elementToClick = document.getElementById(click!);

						if (elementToClick) elementToClick.click();
						else {
							// Sinon on part du principe que c'est un sélecteur CSS
							const elements = document.querySelector(click!);
							if (elements) (elements as HTMLElement).click();
							else
								throw new Error(
									`[${this._.TAG}] L'attribut 'click' ne référence aucun élément.`,
								);
						}
					},
					val,
				);
			}
		}
	}

	/**
	 * Permet de lancer une erreur avec un message spécifique dans une expression inline.
	 * @param msg Message à envoyer dans l'erreur.
	 */
	#_throw(msg: string) {
		throw new Error(msg);
		return null;
	}
	//#endregion Private methods

	//#region Static methods

	/**
	 * Génère un bouton icône avec l'icône spécifiée.
	 * @param icon Icône à afficher dans le bouton.
	 * @returns Node créée.
	 */
	static Create(icon: string): HTMLBnumButtonIcon {
		const node = document.createElement(this.TAG) as HTMLBnumButtonIcon;
		node.appendChild(document.createTextNode(icon));
		return node;
	}

	/**
	 * Génère le code HTML d'un bouton icône avec l'icône spécifiée.
	 * @param icon Icône à afficher dans le bouton.
	 * @returns Code HTML créée.
	 */
	static Write(icon: string, attrs: Record<string, string> = {}): string {
		return <HTMLBnumButtonIcon {...attrs}>{icon}</HTMLBnumButtonIcon>;
	}
	//#endregion Static methods
}
